/**
 * Pre-publication review of streamer images (requirements 5.3-5.5).
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../lib/api-client';
import { formatJstDateTime } from '../../lib/date-utils';
import type { MediaReviewItem } from '../../lib/types';
import ReasonDialog, { type ReasonDialogResult } from './ReasonDialog';

const KIND_LABELS: Record<MediaReviewItem['kind'], string> = {
  profile_image: 'プロフィール画像',
  gallery_image: 'ギャラリー画像',
};

function itemKey(item: MediaReviewItem) {
  return `${item.kind}:${item.item_id}`;
}

function PendingPreview({ item }: { item: MediaReviewItem }) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    apiClient.getModerationMediaObjectUrl(item.preview_path)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        objectUrl = url;
        setSrc(url);
      })
      .catch(() => setFailed(true));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [item]);

  if (failed) return <div className="flex h-40 w-40 items-center justify-center bg-gray-100 text-xs text-gray-500">画像を取得できません</div>;
  if (!src) return <div className="h-40 w-40 animate-pulse bg-gray-100" />;
  return <img src={src} alt={`${item.streamer_username} の${KIND_LABELS[item.kind]}`} className="h-40 w-40 object-cover" />;
}

export default function MediaReviewPanel() {
  const [items, setItems] = useState<MediaReviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<MediaReviewItem | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await apiClient.listPendingMedia());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : '承認待ちの画像を取得できませんでした');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const approve = async (item: MediaReviewItem) => {
    setBusyKey(itemKey(item));
    try {
      await apiClient.approveMedia(item.kind, item.item_id);
      setNotice(`${item.streamer_username} の${KIND_LABELS[item.kind]}を承認し、公開しました。`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : '承認に失敗しました');
    } finally {
      setBusyKey(null);
    }
  };

  const reject = async (input: ReasonDialogResult) => {
    if (!rejecting) return;
    await apiClient.rejectMedia(rejecting.kind, rejecting.item_id, input);
    setNotice(`${rejecting.streamer_username} の${KIND_LABELS[rejecting.kind]}を非承認にし、削除しました。`);
    setRejecting(null);
    await load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold">画像の公開前承認</h3>
        <button type="button" onClick={() => void load()} className="border border-gray-300 px-3 py-1 text-sm">再読み込み</button>
      </div>
      <p className="text-sm text-gray-600">配信者がアップロードした画像は、承認するまで公開されません。禁止コンテンツが含まれていないか確認してください。</p>
      {error && <p className="bg-red-50 p-3 text-sm text-red-700" role="alert">{error}</p>}
      {notice && <p className="bg-green-50 p-3 text-sm text-green-800" role="status">{notice}</p>}
      {loading ? (
        <p className="text-sm text-gray-500">読み込み中...</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500">承認待ちの画像はありません</p>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item) => (
            <li key={itemKey(item)} className="border p-3">
              <PendingPreview item={item} />
              <p className="mt-2 text-sm font-semibold">{item.streamer_display_name || item.streamer_username}（{KIND_LABELS[item.kind]}）</p>
              <p className="text-xs text-gray-500">申請 {formatJstDateTime(item.submitted_at)}</p>
              <div className="mt-3 flex gap-2">
                <button
                  type="button" disabled={busyKey === itemKey(item)} onClick={() => void approve(item)}
                  className="bg-green-700 px-3 py-1 text-sm text-white disabled:opacity-60"
                >
                  承認して公開
                </button>
                <button type="button" onClick={() => setRejecting(item)} className="border border-red-700 px-3 py-1 text-sm text-red-700">
                  非承認（削除）
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <ReasonDialog
        open={rejecting !== null}
        title="画像を非承認にする"
        description="画像は削除され、配信者には非承認になったことが表示されます。"
        confirmLabel="非承認にする"
        onCancel={() => setRejecting(null)}
        onConfirm={reject}
      />
    </div>
  );
}
